import {
  METRICS_DATA,
  MILESTONES,
  FACILITIES,
  PRODUCT_INTEL,
  SURGICAL_INQUIRIES_SINCE_ISO,
} from './constants.js';
import type { Facility, MetricsSnapshot, Milestone, ProductIntel } from './constants.js';
import { normalizeSurgeonName } from './helpers.js';
import type { DashboardData } from '../../../services/missionControl.js';

/**
 * Operational metrics for one time window. Field names mirror what each
 * MetricCard shows on screen and carry their unit, so the export is
 * self-describing for both LLM consumption and database seeding. The
 * snapshot's off-screen `supported` field is intentionally omitted.
 */
export interface MissionControlExportMetrics {
  surgeonsOnboarded: number;
  activeFacilities: number;
  operatingRoomsConnected: number;
  surgicalProfiles: number;
  recordedProcedures: number;
  surgicalInquiries: number;
  /**
   * Share of `surgicalInquiries` raised by internal `@exex.ai` staff — demos and
   * testing rather than customer usage. Included in `surgicalInquiries`, not
   * deducted from it. The one field here that isn't on screen: the card counts
   * internal activity without breaking it out, so the export is where the split
   * is available at all.
   */
  surgicalInquiriesFromInternalStaff: number;
  clinicalStaff: number;
}

export interface MissionControlExportMilestone {
  label: string;
  current: number;
  target: number;
  /** Contribution to the composite score, as a fraction (0–1). */
  weightFraction: number;
  /** How `current`/`target` should be read: currency, percent, decimal, number. */
  format: Milestone['format'];
  prefix?: string;
  suffix?: string;
  /** Progress toward target, clamped to 1 (0–1). */
  progressFraction: number;
}

export interface MissionControlDirectorySummary {
  facilities: number;
  /** Distinct surgeons across all facilities (deduped by normalized name). */
  surgeons: number;
  surgicalProfiles: number;
  recordedProcedures: number;
  specialties: string[];
}

export interface MissionControlExportMetricsByWindow {
  allTime: MissionControlExportMetrics;
  last90Days: MissionControlExportMetrics;
  last30Days: MissionControlExportMetrics;
}

export interface MissionControlExport {
  generatedAt: string;
  /** Date surgical-inquiry tracking began; the all-time count starts here. */
  surgicalInquiriesSince: string;
  /** Commercial facilities only. */
  operationalMetrics: MissionControlExportMetricsByWindow;
  /**
   * Every active facility. Both scopes ship so the snapshot doesn't depend on
   * how the panel's toggle happened to be set.
   */
  operationalMetricsWithLegacy: MissionControlExportMetricsByWindow;
  seriesAReadiness: {
    /** Weighted composite score across all milestones, as a percent (0–100). */
    compositeScorePct: number;
    milestones: MissionControlExportMilestone[];
  };
  productIntel: {
    activeProcedures: number;
    products: ProductIntel[];
  };
  directory: {
    summary: MissionControlDirectorySummary;
    facilities: Facility[];
  };
}

/** Maps a raw metrics snapshot to the self-describing export shape. */
function toExportMetrics(m: MetricsSnapshot): MissionControlExportMetrics {
  return {
    surgeonsOnboarded: m.surgeons,
    activeFacilities: m.facilities,
    operatingRoomsConnected: m.ors,
    surgicalProfiles: m.profiles,
    recordedProcedures: m.recorded,
    surgicalInquiries: m.inquiries,
    surgicalInquiriesFromInternalStaff: m.internalInquiries,
    clinicalStaff: m.staff,
  };
}

/**
 * Builds a self-contained snapshot of every data point shown on the Mission
 * Control screen, excluding the Global Infrastructure and IP Portfolio cards.
 *
 * Pure and DOM-free so it can be unit-tested; falls back to the static
 * constants whenever live dashboard data is unavailable.
 */
export function buildMissionControlExport(
  data: DashboardData | null,
  generatedAt: string
): MissionControlExport {
  const rawMetrics = data?.metrics ?? METRICS_DATA;
  // Matches the service's fallback payload, which reports both scopes alike.
  const rawMetricsWithLegacy = data?.metricsWithLegacy ?? rawMetrics;
  const milestones = data?.milestones ?? MILESTONES;
  const facilities = data?.facilities ?? FACILITIES;
  const products = data?.productIntel ?? PRODUCT_INTEL;
  const activeProcedures = data?.activeProcedures ?? 0;

  const compositeScorePct =
    milestones.reduce((acc, m) => acc + Math.min(m.current / m.target, 1) * m.weight, 0) * 100;

  const allSurgeons = facilities.flatMap((f) => f.surgeons);
  const summary: MissionControlDirectorySummary = {
    facilities: facilities.length,
    surgeons: new Set(allSurgeons.map((s) => normalizeSurgeonName(s.name))).size,
    surgicalProfiles: allSurgeons.reduce((a, s) => a + s.profiles, 0),
    recordedProcedures: allSurgeons.reduce((a, s) => a + s.recorded, 0),
    specialties: [...new Set(allSurgeons.map((s) => s.specialty))].sort(),
  };

  return {
    generatedAt,
    surgicalInquiriesSince: SURGICAL_INQUIRIES_SINCE_ISO,
    operationalMetrics: {
      allTime: toExportMetrics(rawMetrics.all),
      last90Days: toExportMetrics(rawMetrics['90d']),
      last30Days: toExportMetrics(rawMetrics['30d']),
    },
    operationalMetricsWithLegacy: {
      allTime: toExportMetrics(rawMetricsWithLegacy.all),
      last90Days: toExportMetrics(rawMetricsWithLegacy['90d']),
      last30Days: toExportMetrics(rawMetricsWithLegacy['30d']),
    },
    seriesAReadiness: {
      compositeScorePct: Math.round(compositeScorePct),
      milestones: milestones.map((m) => ({
        label: m.label,
        current: m.current,
        target: m.target,
        weightFraction: m.weight,
        format: m.format,
        ...(m.prefix !== undefined ? { prefix: m.prefix } : {}),
        ...(m.suffix !== undefined ? { suffix: m.suffix } : {}),
        progressFraction: Math.min(m.current / m.target, 1),
      })),
    },
    productIntel: {
      activeProcedures,
      products,
    },
    directory: {
      summary,
      facilities,
    },
  };
}

/** Serializes the export and triggers a browser download of the JSON file. */
export function downloadMissionControlExport(payload: MissionControlExport): void {
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const stamp = payload.generatedAt.replace(/[:.]/g, '-');
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `mission-control-${stamp}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
